import { FC, ReactNode, useCallback, useState } from 'react';
import {
  Select,
  NativeSelect,
  MenuItem,
  SelectProps,
  useMediaQuery,
  Theme,
} from '@mui/material';

import { AppFormControl, AppFormControlProps } from 'components/AppFormControl';

type OptionLabel =
  | {
      mobile: string;
      desktop: string | ReactNode;
    }
  | string;

interface Props extends Omit<AppFormControlProps, 'children'> {
  value: number | string;
  onChange: (value: string | number) => void;
  options: { value: number | string; label: OptionLabel; tooltip?: string }[];
  componentProps?: SelectProps;
  disabled?: boolean;
}

export const AppSelectForm: FC<Props> = ({
  label,
  helperText,
  value,
  onChange,
  options,
  componentProps,
  disabled,
  ...formControlProps
}) => {
  const xs = useMediaQuery<Theme>(({ breakpoints }) => breakpoints.only('xs'));
  const [openTooltip, setOpenTooltip] = useState<string | undefined>(undefined);

  const optionProps = useCallback(
    (
      v: string | number,
      t: string | undefined,
    ): {
      value: string | number;
      onMouseEnter: () => void;
      onMouseLeave: () => void;
      onClick: () => void;
    } => ({
      value: v,
      onMouseEnter: () => setOpenTooltip(t),
      onMouseLeave: () => setOpenTooltip(undefined),
      onClick: () => setOpenTooltip(undefined),
    }),
    [],
  );

  return (
    <AppFormControl
      label={label}
      helperText={openTooltip ?? helperText}
      {...formControlProps}
    >
      {xs ? (
        <NativeSelect
          value={value}
          onChange={({ target }) => onChange(target.value)}
          inputProps={{ name: label, id: `${label}-form` }}
          disabled={disabled}
        >
          <option {...optionProps('', '')} />
          {options.map(({ value: v, label: l, tooltip: t }) => (
            <option key={v} {...optionProps(v, t)}>
              {typeof l === 'string' ? l : l.mobile}
            </option>
          ))}
        </NativeSelect>
      ) : (
        <Select
          labelId={`${label}-label`}
          id={`${label}-form`}
          value={value}
          onChange={({ target }) => onChange(target.value as string | number)}
          label={label}
          disabled={disabled}
          {...componentProps}
        >
          {options.map(({ value: v, label: l, tooltip: t }) => (
            <MenuItem key={v} {...optionProps(v, t)}>
              {typeof l === 'string' ? l : l.desktop}
            </MenuItem>
          ))}
        </Select>
      )}
    </AppFormControl>
  );
};
